// Utils
import { getTimestamp } from '../../../common/utils/timeUtil';
import { isEmpty } from '../../../common/utils/immutableHelper';
import { hasShiftKey } from '../../utils/keyboard/keyboardUtility';
import searchPopupStayActivePredicate from '../header/workspaceToolsHeader/boardSearch/searchPopupStayActivePredicate';
import { decrementZoomScale, incrementZoomScale } from '../../canvas/zoom/utils/zoomStepUtils';
import { getMinScale } from '../../canvas/zoom/zoomScaleUtils';

// Singletons
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';

// Selectors
import { activeModalIdSelector, getIsModalOpen } from '../../components/modal/activeModalSelector';
import { activePopupSelector } from '../../components/popupPanel/popupOpenSelector';
import { getCurrentlyEditingId } from '../../element/selection/currentlyEditingSelector';
import { getElementFilterSelector } from '../../element/elementFilter/elementFilterSelector';
import {
    getIsPresentationModeEditingDisabled,
    isPresentationModeEnabledSelector,
} from '../presentation/presentationSelector';
import { canvasZoomScaleSelector, canvasZoomTargetScaleSelector } from '../../canvas/store/canvasZoomSelector';

// Actions
import { modalClosed, modalOpened } from '../../components/modal/modalActions';
import { closeAllPopupsIfRequired, closePopup, openPopup } from '../../components/popupPanel/popupActions';
import { clearElementFilter } from '../../element/elementFilter/elementFilterActions';
import { toggleTheme } from '../../user/account/accountModal/interface/themeSettings/themeActions';
import { deselectAllElements, finishEditingElement } from '../../element/selection/selectionActions';
import { startPresentationMode, endPresentationMode } from '../presentation/presentationActions';
import { startAnnotationMode } from '../../element/drawing/annotation/store/annotationActions';
import { setCanvasZoomTargetScale } from '../../canvas/store/canvasActions';
import { getCanvasZoomContentRectThunk } from '../../canvas/store/canvasBoundingRectActions';

// Constants
import { FAKE_SAVE } from '../../reducers/lastSavedReducer';
import { ModalIds } from '../../components/modal/modalConstants';
import { PopupIds } from '../../components/popupPanel/popupConstants';
import {
    PRESENTATION_MODE_DISABLE_EDITING_KEY,
    PRESENTATION_MODE_FOCUS_MODE_ALLOWED_KEY,
    PRESENTATION_MODE_HIDE_COMMENTS_KEY,
} from '../presentation/presentationConstants';

/**
 * Toggles dark mode on and off.
 */
export const dispatchToggleTheme = (event) => {
    event.stopPropagation();
    event.preventDefault();
    return toggleTheme();
};

export const toggleShortcutsModal = () => (dispatch, getState) => {
    const state = getState();
    const activeModalId = activeModalIdSelector(state);

    if (activeModalId === ModalIds.SHORTCUTS) return dispatch(modalClosed(ModalIds.SHORTCUTS));

    if (activeModalId) return;

    dispatch(modalOpened(ModalIds.SHORTCUTS));
};

export const shortcutStartAnnotationMode = () => (dispatch, getState) => {
    const state = getState();

    dispatch(closeAllPopupsIfRequired());

    // Don't begin annotation mode if a modal is open
    if (activeModalIdSelector(state)) return;

    if (isPresentationModeEnabledSelector(state) && getIsPresentationModeEditingDisabled(state)) return;

    dispatch(startAnnotationMode());
};

export const dispatchDisablePresentationMode = () => (dispatch, getState) => {
    const state = getState();
    const isModalOpen = getIsModalOpen(state);
    const isPresentationModeEnabled = isPresentationModeEnabledSelector(state);
    !isModalOpen && isPresentationModeEnabled && dispatch(endPresentationMode());
};

export const togglePresentationMode = () => (dispatch, getState) => {
    const state = getState();
    const isPresentationModeEnabled = isPresentationModeEnabledSelector(state);

    const getItemFromLocalStorage = (key) => JSON.parse(localStorage.getItem(key));

    const hideComments = getItemFromLocalStorage(PRESENTATION_MODE_HIDE_COMMENTS_KEY) ?? false;
    const disableEditing = getItemFromLocalStorage(PRESENTATION_MODE_DISABLE_EDITING_KEY) || false;
    const isFocusModeAllowed = getItemFromLocalStorage(PRESENTATION_MODE_FOCUS_MODE_ALLOWED_KEY) ?? true;

    if (isPresentationModeEnabled) {
        dispatch(endPresentationMode());
    } else {
        dispatch(startPresentationMode({ hideComments, disableEditing, isFocusModeAllowed }));
    }
};

export const openSearchPopup = () => openPopup(PopupIds.SEARCH, searchPopupStayActivePredicate);

/**
 * Shows the "Saving" text.
 */
export const fakeSave = () => ({ type: FAKE_SAVE, timestamp: getTimestamp() });

/**
 * Handles clearing filters or deselecting elements when escape is pressed.
 */
export const deselectOrClearFilter = (event) => (dispatch, getState) => {
    // TODO-REMOVE-DRAFT: This hack is draft-specific; can be removed when draft is
    // FIXME This is a hack fix to prevent the global escape handler from overriding the mention's plugin
    // escape handler.  I'm not sure why the mentions plugin escape handler is now executed after this handler,
    // I assume that at one point it wasn't because there's custom code in MentionSuggestions/index.js
    // which specifically refers to handling escapes
    const mentionsNode = document.querySelector('.MentionSuggestions');
    if (mentionsNode) return;

    const state = getState();
    const activePopups = activePopupSelector(state);

    if (!isEmpty(activePopups)) {
        const lastOpenedPopup = activePopups.keySeq().last();
        const isModalOpen = getIsModalOpen(state);

        // if there is a popup and a modal open only close the modal
        if (isModalOpen) return dispatch(modalClosed(activeModalIdSelector(state)));
        if (!isModalOpen && lastOpenedPopup) return dispatch(closePopup(lastOpenedPopup, false));
    }

    const elementFilter = getElementFilterSelector(state);
    const hasElementFilter = elementFilter && !elementFilter.isEmpty();

    if (hasElementFilter) return dispatch(clearElementFilter());

    if (hasShiftKey(event)) {
        const editingId = getCurrentlyEditingId(state);
        if (editingId) return dispatch(finishEditingElement(editingId));
    }

    return dispatch(deselectAllElements());
};

// Zoom
export const zoomOut = (event) => (dispatch, getState) => {
    const state = getState();
    const currentScale = canvasZoomTargetScaleSelector(state) || canvasZoomScaleSelector(state);

    const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
    const canvasViewportClientRect = canvasViewportNode.getBoundingClientRect();

    const contentRect = dispatch(getCanvasZoomContentRectThunk(canvasViewportClientRect));

    const minScale = getMinScale(canvasViewportClientRect, contentRect);

    const nextScale = decrementZoomScale(currentScale, minScale);
    dispatch(setCanvasZoomTargetScale({ scale: nextScale }));
};

export const zoomIn = () => (dispatch, getState) => {
    const state = getState();

    const currentScale = canvasZoomTargetScaleSelector(state) || canvasZoomScaleSelector(state);

    const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
    const canvasViewportClientRect = canvasViewportNode.getBoundingClientRect();

    const contentRect = dispatch(getCanvasZoomContentRectThunk(canvasViewportClientRect));

    const minScale = getMinScale(canvasViewportClientRect, contentRect);

    const nextScale = incrementZoomScale(currentScale, minScale);
    dispatch(setCanvasZoomTargetScale({ scale: nextScale }));
};

export const zoomClear = () => (dispatch) => dispatch(setCanvasZoomTargetScale({ scale: 1 }));

/**
 * If the user has the zoom feature enabled they will toggle between 100% and fully zoomed out.
 * Otherwise they'll use the legacy canvas scale feature.
 */
export const zoomToggleScale = () => (dispatch, getState) => {
    const state = getState();

    const currentScale = canvasZoomTargetScaleSelector(state) || canvasZoomScaleSelector(state);

    const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
    const canvasViewportClientRect = canvasViewportNode.getBoundingClientRect();

    const contentRect = dispatch(getCanvasZoomContentRectThunk(canvasViewportClientRect));

    const minScale = getMinScale(canvasViewportClientRect, contentRect);

    // If the current scale is 100% or less than 100% but not equal to the min scale then go to the min scale,
    // otherwise return to 100%
    const nextScale = currentScale === 1 || (currentScale < 1 && currentScale !== minScale) ? minScale : 1;
    dispatch(setCanvasZoomTargetScale({ scale: nextScale }));
};
